import { useState, useEffect } from "react";
import axios from "axios";
import { Link2, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SmartThingsOAuth() {
  const [oauthStatus, setOauthStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOAuthStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/smartthings/status`);
      setOauthStatus(response.data);
    } catch (error) {
      console.error("Error fetching OAuth status:", error);
      toast.error("Gagal mendapatkan status OAuth");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOAuthStatus();
  }, []);

  const handleConnect = () => {
    // Open OAuth authorization in new window
    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    const authWindow = window.open(
      `${API}/auth/smartthings/authorize`,
      'SmartThings Authorization',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Check if authorization completed
    const checkInterval = setInterval(() => {
      if (authWindow.closed) {
        clearInterval(checkInterval);
        // Refresh status after window closed
        setTimeout(() => {
          fetchOAuthStatus();
          toast.success("Checking authorization status...");
        }, 1000);
      }
    }, 500);
  };

  const handleRefresh = async () => {
    try {
      const response = await axios.post(`${API}/auth/smartthings/refresh`);
      
      if (response.data.success) {
        toast.success("Token berhasil di-refresh!");
        fetchOAuthStatus();
      } else {
        toast.error(response.data.error || "Gagal refresh token");
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      toast.error("Gagal refresh token");
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-lg border-white/20">
        <CardContent className="py-20 text-center">
          <Loader2 className="h-8 w-8 text-white animate-spin mx-auto" />
          <p className="text-white mt-4">Loading OAuth status...</p>
        </CardContent>
      </Card>
    );
  }

  const isConnected = oauthStatus?.status === 'active';

  return (
    <Card className="bg-white/10 backdrop-blur-lg border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          {isConnected ? (
            <CheckCircle className="h-6 w-6 text-green-400" />
          ) : (
            <AlertCircle className="h-6 w-6 text-yellow-400" />
          )}
          SmartThings OAuth Connection
        </CardTitle>
        <CardDescription className="text-blue-200">
          {isConnected ? 'Connected and authorized' : 'Not connected'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isConnected ? (
          <>
            {/* Connected Status */}
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-green-400 font-medium">Connected</span>
              </div>
              <div className="text-sm text-white space-y-1">
                <p>Token Type: OAuth2</p>
                <p>Hours Remaining: {oauthStatus.hours_remaining?.toFixed(2)}</p>
                <p>Last Refresh: {new Date(oauthStatus.last_refresh).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' })}</p>
                <p>Expires At: {new Date(oauthStatus.expires_at).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' })}</p>
              </div>
            </div>

            {/* Refresh Button */}
            <Button
              onClick={handleRefresh}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Refresh Token
            </Button>

            {/* Info */}
            <div className="text-xs text-blue-200 text-center">
               Token will auto-refresh before expiry
            </div>
          </>
        ) : (
          <>
            {/* Not Connected Status */}
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
                <span className="text-yellow-400 font-medium">Not Connected</span>
              </div>
              <p className="text-sm text-white">
                Connect your SmartThings account to enable smart plug control
              </p>
            </div>

            {/* Connect Button */}
            <Button
              onClick={handleConnect}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Link2 className="h-4 w-4 mr-2" />
              Connect SmartThings
            </Button>

            {/* Instructions */}
            <div className="text-xs text-blue-200 space-y-1">
              <p>• Click "Connect SmartThings" button</p>
              <p>• Login to your Samsung account</p>
              <p>• Approve permissions</p>
              <p>• You'll be redirected back automatically</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
